<template>
  <Transition name="slide-down">
    <div
      v-if="isVisible"
      class="app-update-banner"
      data-testid="app-update-banner"
    >
      <div class="app-update-banner__container">
        <!-- Иконка обновления -->
        <div class="app-update-banner__icon">
          <Icon name="heroicons:arrow-down-tray" class="w-5 h-5" />
        </div>

        <!-- Основной контент -->
        <div class="app-update-banner__content">
          <div class="app-update-banner__title">
            <Icon name="heroicons:sparkles" class="w-4 h-4 inline mr-1" />
            {{ banner.isForced.value ? 'Требуется обновление приложения' : 'Доступна новая версия приложения' }}
          </div>

          <div class="app-update-banner__subtitle">
            <Icon name="heroicons:arrow-path" class="w-3.5 h-3.5 inline mr-1" />
            {{ subtitle }}
          </div>
        </div>

        <!-- Действия -->
        <div class="app-update-banner__actions">
          <button type="button" class="app-update-banner__update" @click="openUpdate">
            Обновить
          </button>

          <button
            v-if="!banner.isForced.value"
            type="button"
            class="app-update-banner__dismiss"
            aria-label="Скрыть уведомление об обновлении"
            @click="banner.dismiss()"
          >
            <Icon name="heroicons:x-mark" class="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { Capacitor } from '@capacitor/core'
import consola from 'consola'
import { computed } from 'vue'
import { useBranchAvailability, useInstitutionStore, useWorkingHours } from '@entities/institution'
import { useAppUpdateBanner } from '@features/app-update'

const banner = useAppUpdateBanner()
const storeInstitution = useInstitutionStore()
const workingHours = useWorkingHours()
const availability = useBranchAvailability()

/**
 * Аварийные баннеры важнее предложения обновиться: закрытое заведение,
 * остановленный приём заказов и приостановленная оплата объясняют гостю,
 * почему заказ прямо сейчас не оформляется. Два баннера подряд спорят за
 * внимание и сдвигают контент, поэтому обновление уступает место.
 *
 * Исключение — принудительное обновление: с ним приложением всё равно не
 * пользоваться, и свой баннер оно не отдаёт никому.
 */
const isCriticalBannerShown = computed(
  () =>
    !workingHours.isCurrentBranchOpen.value ||
    availability.isFullyBlocked.value ||
    availability.deliveryStopped.value ||
    Boolean(storeInstitution.institution?.payment_paused),
)

const isVisible = computed(
  () => banner.isVisible.value && (banner.isForced.value || !isCriticalBannerShown.value),
)

const subtitle = computed(() => {
  const version = banner.latestVersion.value
  return version
    ? `Версия ${version} — обновите, чтобы всё работало корректно`
    : 'Обновите приложение, чтобы всё работало корректно'
})

/** Ссылка на магазин из настроек заведения (админка → «Мобильное приложение») */
const storeUrl = computed(() => {
  const institution = storeInstitution.institution
  return Capacitor.getPlatform() === 'ios'
    ? institution?.app_store_url || null
    : institution?.google_play_url || null
})

const openUpdate = async () => {
  // Веб — новая версия приезжает обычной перезагрузкой страницы
  if (!Capacitor.isNativePlatform()) {
    window.location.reload()
    return
  }

  const url = storeUrl.value
  if (!url) {
    consola.warn('[app-update-banner] Нет ссылки на магазин для платформы', Capacitor.getPlatform())
    return
  }

  try {
    const { Browser } = await import('@capacitor/browser')
    await Browser.open({ url })
  } catch (error) {
    consola.warn('[app-update-banner] Не удалось открыть магазин:', error)
  }
}
</script>

<style scoped lang="scss">
// Баннер «доступно обновление» — состояние primary из дизайн-системы (hui).
// Тот же лейаут, что у «Сейчас мы закрыты», отличается только цветом фона (синий вместо янтарного).
.app-update-banner {
  @apply relative bg-hui-primary text-hui-primary-foreground;
  @apply border-b border-black/10 shadow-sm;

  &__container {
    @apply max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5;
    @apply flex items-center justify-between gap-3;
  }

  &__icon {
    @apply flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-white/20;
  }

  &__content {
    @apply flex-1 min-w-0 text-center;
  }

  &__title {
    @apply flex items-center justify-center text-sm font-bold mb-0.5;
  }

  &__subtitle {
    @apply flex items-center justify-center text-xs font-medium opacity-90;
  }

  &__actions {
    @apply flex-shrink-0 flex items-center gap-2;
  }

  &__update {
    @apply px-3 py-1.5 rounded-full text-xs font-semibold;
    @apply bg-white/20 hover:bg-white/30 transition-colors;
  }

  &__dismiss {
    @apply flex items-center justify-center w-9 h-9 rounded-full;
    @apply bg-white/20 hover:bg-white/30 transition-colors;
  }
}

/* Анимация появления */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

/* Компактнее на мобильных */
@media (max-width: 640px) {
  .app-update-banner {
    &__container {
      @apply px-3 py-2 gap-2;
    }

    &__icon {
      @apply hidden;
    }

    &__dismiss {
      @apply w-8 h-8;
    }
  }
}
</style>
